import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { gatewaysApi, devicesApi, clientsApi } from "@/lib/apiClient";
import { PageContainer, PageHeader } from "@/components/Layout";
import { Pager, usePager } from "@/components/Pager";
import { Mono, CodePill } from "@/components/Atoms";
import { Search } from "lucide-react";

export default function Messages() {
  const [filters, setFilters] = useState({
    client_id: "",
    device_mac: "",
    gw_mac: "",
    from: "",
    to: "",
  });

  // Backend-side filtering preferred; FE-side fallback if backend ignores params.
  const list = useQuery({
    queryKey: ["messages", filters],
    queryFn: () =>
      gatewaysApi.messages({
        client_id: filters.client_id || undefined,
        device_mac: filters.device_mac || undefined,
        gw_mac: filters.gw_mac || undefined,
        from: filters.from ? new Date(filters.from).toISOString() : undefined,
        to: filters.to ? new Date(filters.to).toISOString() : undefined,
      }),
  });

  const clients = useQuery({ queryKey: ["clients"], queryFn: clientsApi.list });
  const devices = useQuery({ queryKey: ["devices"], queryFn: devicesApi.list });
  const gateways = useQuery({ queryKey: ["gateways"], queryFn: () => gatewaysApi.list() });

  // Defensive FE filter — backend may not honor every param yet.
  const rows = (list.data || []).filter((m) => {
    if (filters.device_mac && (m.device_mac || "").toLowerCase() !== filters.device_mac.toLowerCase()) return false;
    if (filters.gw_mac && (m.gw_mac || "").toLowerCase() !== filters.gw_mac.toLowerCase()) return false;
    if (filters.client_id && m.client_id !== filters.client_id) return false;
    if (filters.from) {
      if (new Date(m.received_at).getTime() < new Date(filters.from).getTime()) return false;
    }
    if (filters.to) {
      if (new Date(m.received_at).getTime() > new Date(filters.to).getTime()) return false;
    }
    return true;
  });

  const pager = usePager(rows, { defaultSize: 50, sizes: [25, 50, 100] });

  return (
    <PageContainer>
      <PageHeader title="Messages" subtitle="All gateway-emitted messages across every client. Filter, paginate, inspect parsed payloads." />

      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200 grid grid-cols-1 md:grid-cols-6 gap-3">
          <select
            data-testid="msg-filter-client"
            value={filters.client_id}
            onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}
            className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">All clients</option>
            {clients.data?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            data-testid="msg-filter-device"
            value={filters.device_mac}
            onChange={(e) => setFilters({ ...filters, device_mac: e.target.value })}
            className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">All devices</option>
            {devices.data?.map((d) => <option key={d.id} value={d.mac}>{d.name} · {d.mac}</option>)}
          </select>
          <select
            data-testid="msg-filter-gateway"
            value={filters.gw_mac}
            onChange={(e) => setFilters({ ...filters, gw_mac: e.target.value })}
            className="border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white"
          >
            <option value="">All gateways</option>
            {gateways.data?.map((g) => <option key={g.id} value={g.mac}>{g.name} · {g.mac}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-zinc-500">
            from
            <input
              data-testid="msg-filter-from"
              type="datetime-local"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
              className="border border-zinc-200 rounded-md px-2 py-1.5 text-xs flex-1"
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-zinc-500">
            to
            <input
              data-testid="msg-filter-to"
              type="datetime-local"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
              className="border border-zinc-200 rounded-md px-2 py-1.5 text-xs flex-1"
            />
          </label>
          <button
            data-testid="msg-filter-clear"
            onClick={() => setFilters({ client_id: "", device_mac: "", gw_mac: "", from: "", to: "" })}
            className="border border-zinc-200 rounded-md px-3 py-2 text-sm hover:bg-zinc-50"
          >Clear</button>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left p-3 font-semibold">Received</th>
              <th className="text-left p-3 font-semibold">GW MAC</th>
              <th className="text-left p-3 font-semibold">Device MAC</th>
              <th className="text-left p-3 font-semibold">Flag</th>
              <th className="text-left p-3 font-semibold">Payload (hex)</th>
              <th className="text-left p-3 font-semibold">Parsed</th>
            </tr>
          </thead>
          <tbody>
            {pager.pageRows.map((m) => (
              <tr key={m.id} className="border-t border-zinc-100 hover:bg-zinc-50/60" data-testid={`msg-row-${m.id}`}>
                <td className="p-3 text-xs font-mono text-zinc-500">{new Date(m.received_at).toLocaleString()}</td>
                <td className="p-3"><Mono>{m.gw_mac}</Mono></td>
                <td className="p-3"><Mono>{m.device_mac || "—"}</Mono></td>
                <td className="p-3"><CodePill>{m.flag}</CodePill></td>
                <td className="p-3"><Mono className="text-zinc-600 truncate block max-w-[420px]">{m.payload_hex}</Mono></td>
                <td className="p-3"><Mono className="text-zinc-700 truncate block max-w-[300px]">{m.parsed ? JSON.stringify(m.parsed) : "—"}</Mono></td>
              </tr>
            ))}
            {pager.total === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-sm text-zinc-500">
                {list.isLoading ? "Loading…" : "No messages match the filters."}
              </td></tr>
            )}
          </tbody>
        </table>

        <Pager {...pager} testidPrefix="msg-pager" />
      </div>
    </PageContainer>
  );
}
